import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { X, CheckCheck, Bell, Volume2, VolumeX, Play, ChevronDown, BellRing } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  testPlayNotificationSound, hasPendingPlay, onPendingPlayChange, flushPendingPlay,
} from '@/lib/notificationSound';

function timeAgo(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

const CLIENT_ROLES = new Set(['Client', 'Client Manager']);

/**
 * Compact per-user "Sound" settings embedded in the Notification panel.
 *
 * - Only rendered for staff users (never for Client / Client Manager).
 * - All three controls (on/off, sound choice, volume) persist to
 *   `/api/me/notification-prefs` immediately. The Test button plays the
 *   current selection without saving (useful for comparing choices).
 * - The surrounding panel keeps its existing dark/amber styling to avoid
 *   touching the global theme — these controls match that aesthetic.
 */
function SoundSettings({ user, onUpdated }) {
  const prefs = user?.notification_prefs || {};
  const [soundOn,   setSoundOn]   = useState(prefs.sound_on !== false);
  const [choice,    setChoice]    = useState(prefs.sound_choice || 'pikachu');
  const [volume,    setVolume]    = useState(
    typeof prefs.volume === 'number' ? prefs.volume : 0.7,
  );
  const [open,      setOpen]      = useState(false);
  const [saving,    setSaving]    = useState(false);

  // Keep local state in sync if the user doc changes (e.g. page refresh).
  useEffect(() => {
    setSoundOn(prefs.sound_on !== false);
    setChoice(prefs.sound_choice || 'pikachu');
    setVolume(typeof prefs.volume === 'number' ? prefs.volume : 0.7);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs.sound_on, prefs.sound_choice, prefs.volume]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await api.patch('/me/notification-prefs', patch);
      onUpdated && onUpdated(r.data);
    } catch { /* silent — settings UI should never crash */ }
    finally   { setSaving(false); }
  };

  const toggle = () => {
    const next = !soundOn;
    setSoundOn(next);
    save({ sound_on: next });
  };

  const pickChoice = (v) => {
    setChoice(v);
    setOpen(false);
    save({ sound_choice: v });
  };

  const onVolume = (e) => {
    const v = Math.max(0, Math.min(1, Number(e.target.value) / 100));
    setVolume(v);
    // Debounced save: fire-and-forget on every change (small payload).
    save({ volume: v });
  };

  const test = () =>
    testPlayNotificationSound({
      user,
      sound_choice: choice,
      volume,
    });

  const choiceLabel = choice === 'chime' ? 'Soft chime' : 'Pikachu (default)';

  return (
    <div className="border-t border-amber-500/10 bg-black/20 p-3 text-xs" data-testid="notif-sound-settings">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-amber-200">
          {soundOn ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
          <span className="font-semibold">Sound</span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={soundOn}
          onClick={toggle}
          disabled={saving}
          className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
            soundOn ? 'bg-amber-400' : 'bg-slate-600'
          }`}
          data-testid="notif-sound-toggle"
        >
          <span
            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${
              soundOn ? 'translate-x-4' : 'translate-x-0'
            }`}
          />
        </button>
      </div>

      {/* Sound choice */}
      <div className="relative mb-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          disabled={!soundOn}
          className="w-full flex items-center justify-between rounded-md border border-amber-500/20 bg-white/5 px-2.5 py-1.5 text-slate-200 disabled:opacity-50"
          data-testid="notif-sound-choice"
        >
          <span>{choiceLabel}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </button>
        {open && (
          <div
            className="absolute left-0 right-0 top-full mt-1 rounded-md border border-amber-500/20 bg-[#0a1628] shadow-xl z-10"
            role="menu"
          >
            {[
              { key: 'pikachu', label: 'Pikachu (default)' },
              { key: 'chime',   label: 'Soft chime' },
            ].map((o) => (
              <button
                key={o.key}
                type="button"
                onClick={() => pickChoice(o.key)}
                className={`w-full text-left px-3 py-2 hover:bg-white/5 ${
                  choice === o.key ? 'text-amber-300' : 'text-slate-200'
                }`}
                data-testid={`notif-sound-choice-${o.key}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Volume */}
      <div className="flex items-center gap-2 mb-2">
        <span className="text-slate-400 w-14">Volume</span>
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={Math.round(volume * 100)}
          onChange={onVolume}
          disabled={!soundOn}
          className="flex-1 accent-amber-400"
          aria-label="Notification volume"
          data-testid="notif-sound-volume"
        />
        <span className="font-mono text-[10px] text-slate-400 w-7 text-right">
          {Math.round(volume * 100)}
        </span>
      </div>

      {/* Test button */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={test}
          disabled={!soundOn}
          className="h-7 text-xs bg-transparent border-amber-500/30 text-amber-200 hover:bg-amber-500/10"
          data-testid="notif-sound-test"
        >
          <Play className="h-3 w-3 mr-1" /> Test
        </Button>
      </div>
    </div>
  );
}

export default function NotificationPanel({ onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingPlay, setPendingPlay] = useState(hasPendingPlay());
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const isClientRole = CLIENT_ROLES.has(user?.role);

  // Keep the "Tap to enable sound" hint reactive to the sound layer.
  useEffect(() => {
    setPendingPlay(hasPendingPlay());
    return onPendingPlayChange(setPendingPlay);
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/notifications');
      setItems(res.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const markAll = async () => {
    await api.post('/notifications/read-all');
    load();
  };

  const open = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    if (n.link) { onClose(); navigate(n.link); }
  };

  const onPrefsUpdated = (nextPrefs) => {
    // Update the auth-context user so the sound hook picks up the change on
    // the next poll. If AuthContext doesn't expose `setUser`, do nothing —
    // the preference still round-trips on page refresh via /auth/me.
    if (typeof setUser === 'function' && user) {
      setUser({ ...user, notification_prefs: nextPrefs });
    }
  };

  return (
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-[#0a1628] border-l border-amber-500/20 shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="notif-panel"
      >
        <div className="p-4 border-b border-amber-500/10 flex items-center justify-between">
          <div className="flex items-center gap-2 text-amber-200">
            <Bell className="h-4 w-4" />
            <h3 className="font-semibold">Notifications</h3>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={markAll} className="text-amber-200 hover:bg-amber-500/10">
              <CheckCheck className="h-4 w-4 mr-1" />
              <span className="text-xs">Mark all</span>
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose} className="text-amber-200 hover:bg-amber-500/10">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {/* "Tap here to enable sound" — shown only to staff when a new
            notification already arrived but browser audio is still locked
            (no user gesture yet in this session). Clicking the hint IS a
            gesture — it unlocks audio and plays the pending sound. */}
        {!isClientRole && pendingPlay && (
          <button
            type="button"
            onClick={() => { flushPendingPlay(); setPendingPlay(false); }}
            className="mx-3 mt-2 mb-1 flex items-center gap-2 rounded-md border border-amber-400/30 bg-amber-500/15 hover:bg-amber-500/25 px-3 py-2 text-left text-xs text-amber-100"
            data-testid="notif-sound-unlock-hint"
          >
            <BellRing className="h-4 w-4 flex-shrink-0" />
            <span>Tap here to enable sound for new notifications</span>
          </button>
        )}
        <div className="flex-1 overflow-y-auto scrollbar-thin">
          {loading ? (
            <div className="p-6 text-center text-slate-400">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-slate-400">
              <Bell className="h-8 w-8 mx-auto mb-3 opacity-40" />
              No notifications yet
            </div>
          ) : (
            <ul className="divide-y divide-amber-500/5">
              {items.map((n) => (
                <li
                  key={n.id}
                  onClick={() => open(n)}
                  className={`p-4 cursor-pointer hover:bg-white/5 transition ${!n.read ? 'bg-amber-500/5' : ''}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-white">{n.title}</div>
                      <div className="text-xs text-slate-300 mt-1 line-clamp-2">{n.body}</div>
                    </div>
                    {!n.read && <span className="h-2 w-2 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-2">{timeAgo(n.created_at)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
        {/* Staff-only sound settings. Client / Client Manager roles don't
            see this block at all — they only get the tab-title "(N)" count. */}
        {user && !isClientRole && (
          <SoundSettings user={user} onUpdated={onPrefsUpdated} />
        )}
      </div>
    </div>
  );
}
